import { useState, useEffect } from "react";
import { getTagById, getDevicesByTag, getAppsByTag, getPoliciesByTag } from "@/services/tagsApi";
import { mapApiTag, MappedTag } from "@/utils/tagMapping";

interface ResourceCounts {
  devices: number;
  profiles: number;
  applications: number;
  configurations: number;
  patchPolicies: number;
  users: number;
}

const getUniqueResourceCount = (items: any[], keys: string[]) => {
  const unique = new Set<string>();

  items.forEach((item) => {
    const rawKey = keys
      .map((key) => item?.[key])
      .find((value) => value !== undefined && value !== null && String(value).trim() !== "");

    if (rawKey !== undefined && rawKey !== null) {
      unique.add(String(rawKey));
    }
  });

  return unique.size;
};

export const useTagDetail = (tagId: string) => {
  const [tag, setTag] = useState<MappedTag | null>(null);
  const [resourceCounts, setResourceCounts] = useState<ResourceCounts>({
    devices: 0, profiles: 0, applications: 0, configurations: 0, patchPolicies: 0, users: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTagDetail = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [apiTag, devices, apps, policies] = await Promise.all([
        getTagById(tagId),
        getDevicesByTag(tagId).catch(() => []),
        getAppsByTag(tagId).catch(() => []),
        getPoliciesByTag(tagId).catch(() => []),
      ]);
      setTag(mapApiTag(apiTag));
      setResourceCounts({
        devices: getUniqueResourceCount(Array.isArray(devices) ? devices : [], ["uuid", "id", "deviceId", "udid", "serialNumber", "name", "deviceName"]),
        profiles: getUniqueResourceCount(Array.isArray(policies) ? policies : [], ["id", "policyId", "uuid", "name", "policyName"]),
        applications: getUniqueResourceCount(Array.isArray(apps) ? apps : [], ["id", "appId", "uuid", "bundleId", "name", "appName"]),
        configurations: 0,
        patchPolicies: 0,
        users: 0,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (tagId) fetchTagDetail();
  }, [tagId]);

  return { tag, resourceCounts, isLoading, error, refetch: fetchTagDetail };
};
